import { action, computed } from 'mobx';

import { Category } from '../../../types/api';
import { ApiClient } from '../apiClient/ApiClient';
import { ErrorReporter } from '../errorReporter/ErrorReporter';
import { ConfigStore } from './ConfigStore';

export interface CategoriesStoreState {
  bookName: string | null;
  categories: Category[] | null;
}

export interface CategoryGroup {
  shortDisplayName?: string;
  displayName: string;
  categories: Category[];
}

export class CategoriesStore {
  private apiClient: ApiClient;
  private errorReporter: ErrorReporter;
  private readonly configStore: ConfigStore;
  public bookName: string | null = null;
  public categories: Category[] | null = null;

  constructor(
    {
      apiClient,
      errorReporter,
      configStore,
    }: { apiClient: ApiClient; errorReporter: ErrorReporter; configStore: ConfigStore },
    initialState?: CategoriesStoreState,
  ) {
    this.apiClient = apiClient;
    this.errorReporter = errorReporter;
    this.configStore = configStore;
    if (initialState) {
      this.setCategories(initialState.bookName, initialState.categories);
    }
  }

  @computed
  private get allCategoriesBySlug(): Map<string, Category> {
    const categories = this.categories || [];
    const allCategories = [...categories];

    categories
      .filter(({ subCategories }) => subCategories)
      .forEach(({ subCategories }) => allCategories.push(...subCategories));

    return new Map(allCategories.map((item) => [item.name, item]));
  }

  public getParentCategoryBySlug(slug: string): Category | undefined {
    return this.categories.find(
      ({ subCategories }) => subCategories && subCategories.some(({ name }) => name === slug),
    );
  }

  @computed
  public get sortCategories(): Category[] {
    return Array.from(this.allCategoriesBySlug.values()).filter(({ type }) => type === 'sort');
  }

  public get superCategories(): CategoryGroup[] {
    return [
      {
        displayName: 'Business & Services',
        shortDisplayName: 'Business',
        categories: [
          this.getCategoryBySlug('business'),
          this.getCategoryBySlug('health-wellness'),
          this.getCategoryBySlug('beauty-and-hair'),
          this.getCategoryBySlug('fashion-and-style'),
          this.getCategoryBySlug('travel-tourism'),
          this.getCategoryBySlug('restaurants-food'),
          this.getCategoryBySlug('landing-pages'),
          this.getCategoryBySlug('professional-cv'),
        ],
      },
      {
        displayName: 'Store',
        categories: [this.getCategoryBySlug('online-store')],
      },
      {
        displayName: 'Creative',
        categories: [
          this.getCategoryBySlug('photography'),
          this.getCategoryBySlug('design'),
          this.getCategoryBySlug('music'),
          this.getCategoryBySlug('creative-arts'),
          this.getCategoryBySlug('portfolio-cv'),
          this.getCategoryBySlug('video'),
        ],
      },
      {
        displayName: 'Community',
        categories: [
          this.getCategoryBySlug('education'),
          this.getCategoryBySlug('communities'),
          this.getCategoryBySlug('events'),
        ],
      },
      {
        displayName: 'Blog',
        categories: [this.getCategoryBySlug('blog')],
      },
    ];
  }

  @action
  public getCategoryBySlug = (slug: string): Category | undefined => this.allCategoriesBySlug.get(slug);

  @action
  public async initialFetch() {
    try {
      const { bookName, items } = await this.apiClient.fetchCategories();
      this.setCategories(bookName, items);
    } catch (error) {
      this.errorReporter.reportError(error);
      this.setCategories(null, null);
    }
  }

  @action
  private setCategories(bookName: string | null, categories: Category[] | null) {
    this.bookName = bookName;
    this.categories = categories;
  }

  serialize(): CategoriesStoreState {
    return {
      bookName: this.bookName,
      categories: this.categories,
    };
  }

  public getCategoryIds(slugs: string[]) {
    return slugs
      .filter((slug) => {
        if (!this.allCategoriesBySlug.has(slug)) {
          this.errorReporter.reportError(new Error(`Can't find category with slug "${slug}"`));
          return false;
        }
        return true;
      })
      .map((slug) => this.allCategoriesBySlug.get(slug).categoryId);
  }
}
